import React, { useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import { Trophy, X } from '@phosphor-icons/react';

const eur = (v) =>
  new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));

// Fogo de artifício contínuo durante alguns segundos.
function launchFireworks(duration = 6000) {
  const end = Date.now() + duration;
  const colors = ['#F59E0B', '#10B981', '#EF4444', '#3B82F6', '#FFFFFF', '#A855F7'];
  const frame = () => {
    confetti({ particleCount: 6, angle: 60, spread: 70, origin: { x: 0, y: 0.9 }, colors, startVelocity: 55, zIndex: 10000 });
    confetti({ particleCount: 6, angle: 120, spread: 70, origin: { x: 1, y: 0.9 }, colors, startVelocity: 55, zIndex: 10000 });
    if (Math.random() > 0.6) {
      confetti({
        particleCount: 90,
        spread: 360,
        startVelocity: 35,
        ticks: 90,
        gravity: 0.9,
        scalar: 1.1,
        origin: { x: 0.15 + Math.random() * 0.7, y: 0.15 + Math.random() * 0.4 },
        colors,
        zIndex: 10000,
      });
    }
    if (Date.now() < end) requestAnimationFrame(frame);
  };
  frame();
}

// Aplauso sintetizado via Web Audio API (sem dependência de ficheiros).
function playApplause(duration = 5) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return () => {};
    const ctx = new Ctx();
    const now = ctx.currentTime;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0, now);
    master.gain.linearRampToValueAtTime(0.9, now + 0.4);
    master.gain.setValueAtTime(0.9, now + duration - 1);
    master.gain.linearRampToValueAtTime(0.0, now + duration);
    master.connect(ctx.destination);

    // Ruído branco base (multidão de palmas).
    const bufLen = Math.floor(ctx.sampleRate * duration);
    const buffer = ctx.createBuffer(1, bufLen, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufLen; i++) {
      const t = i / ctx.sampleRate;
      // Palmas aleatórias: picos curtos sobre ruído de fundo.
      const clap = Math.random() < 0.004 ? (Math.random() * 2 - 1) : 0;
      const bg = (Math.random() * 2 - 1) * 0.12;
      const env = 0.6 + 0.4 * Math.sin(t * 6);
      data[i] = (clap + bg) * env;
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;

    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 900;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 6000;

    src.connect(hp);
    hp.connect(lp);
    lp.connect(master);
    src.start(now);
    src.stop(now + duration);

    return () => {
      try { src.stop(); ctx.close(); } catch (_) { /* noop */ }
    };
  } catch (_) {
    return () => {};
  }
}

export default function IncentivoCelebration({ campaign, onClose }) {
  const stopAudioRef = useRef(null);

  useEffect(() => {
    launchFireworks(6000);
    stopAudioRef.current = playApplause(5);
    return () => { if (stopAudioRef.current) stopAudioRef.current(); };
  }, []);

  if (!campaign) return null;

  return (
    <div
      data-testid="incentivo-celebration"
      className="fixed inset-0 z-[9999] grid place-items-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-3xl bg-white p-8 text-center shadow-2xl animate-[pop_0.5s_cubic-bezier(0.34,1.56,0.64,1)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="incentivo-celebration-close"
          onClick={onClose}
          className="absolute right-4 top-4 text-[#9CA3AF] hover:text-[#0F1829] transition-colors"
          aria-label="Fechar"
        >
          <X size={22} />
        </button>

        <div className="mx-auto mb-4 grid h-20 w-20 place-items-center rounded-full bg-gradient-to-br from-amber-300 to-amber-500 shadow-lg animate-bounce">
          <Trophy size={44} weight="fill" className="text-white" />
        </div>

        <h2 className="text-2xl font-extrabold text-[#0F1829]">Parabéns! 🎉</h2>
        <p className="mt-2 text-[#374151]">
          Atingiu o objetivo da campanha
          <span className="block font-bold text-amber-700">{campaign.nome}</span>
        </p>

        {campaign.premio && (
          <div className="mt-4 rounded-2xl bg-amber-50 border border-amber-200 p-4">
            <p className="text-[11px] uppercase tracking-wide text-amber-700">O seu prémio</p>
            <p className="text-lg font-bold text-amber-800">🎁 {campaign.premio}</p>
          </div>
        )}

        <div className="mt-4 text-sm text-[#6B7280]">
          Volume conseguido:{' '}
          <b className="text-[#0F1829]">{eur(campaign.conseguido)}</b>
          {' '}de{' '}{eur(campaign.valor_objetivo)}
        </div>

        <button
          data-testid="incentivo-celebration-ok"
          onClick={onClose}
          className="mt-6 w-full rounded-xl bg-[#0F1829] py-3 font-semibold text-white hover:bg-[#1c2a45] transition-colors"
        >
          Continuar
        </button>
      </div>

      <style>{`@keyframes pop{0%{opacity:0;transform:scale(0.8)}100%{opacity:1;transform:scale(1)}}`}</style>
    </div>
  );
}
